import { useMemo, useState, useRef, useEffect, type ReactNode } from 'react';
import { useOrganization } from '../contexts/organization-context';
import ChevronDown from '@/components/ChevronDown';
import Avatar from './Avatar';
import { useNavigate } from 'react-router-dom';
import { IconChevronDown as ChevronDownIcon } from '@tabler/icons-react';

export type OrganizationSelectorMenuItem = {
    key: string;
    label: string;
    icon?: ReactNode;
    active?: boolean;
    danger?: boolean;
    // Renders a divider directly above this item (topbar variant).
    divider?: boolean;
    onClick: () => void;
};

interface OrganizationSelectorProps {
    // 'sidebar' keeps the original full-width layout used inside the sidebar/mobile drawer.
    // 'topbar' renders a compact right-aligned trigger, for use in a horizontal top nav.
    variant?: 'sidebar' | 'topbar';
    // Shown at the top of the dropdown when provided (topbar variant).
    email?: string;
    // Shown below the organizations list in the dropdown (topbar variant).
    menuItems?: OrganizationSelectorMenuItem[];
    // Shown above the organizations list in the dropdown (topbar variant).
    topMenuItems?: OrganizationSelectorMenuItem[];
    // Which edge of the trigger the dropdown panel hangs from (topbar variant only).
    align?: 'left' | 'right';
    // Trigger shows only the org logo, no name or chevron (topbar variant only).
    logoOnly?: boolean;
}

export default function OrganizationSelector({ variant = 'sidebar', menuItems, topMenuItems, align = 'right', logoOnly = false }: OrganizationSelectorProps) {
    const navigate = useNavigate();
    const {
        selectedOrganizerId,
        setSelectedOrganizerId,
        userOwnOrganizer,
        accessibleOrganizers,
        isLoading
    } = useOrganization();

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isTopbar = variant === 'topbar';

    // Prepare all organizations with full data
    const allOrganizations = useMemo(() => {
        const orgs: any[] = [];

        if (userOwnOrganizer) {
            orgs.push({
                ...userOwnOrganizer,
                isOwn: true
            });
        }

        if (accessibleOrganizers?.data?.length > 0) {
            accessibleOrganizers.data.forEach((org: any) => {
                orgs.push({
                    ...org,
                    isOwn: false
                });
            });
        }

        return orgs;
    }, [userOwnOrganizer, accessibleOrganizers]);

    // Get selected organization
    const selectedOrg = useMemo(() => {
        return allOrganizations.find(org => org._id === selectedOrganizerId);
    }, [allOrganizations, selectedOrganizerId]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    if (isLoading) {
        return (
            <div className={isTopbar ? 'w-48' : 'w-full mb-4'}>
                <div className={`flex items-center gap-2 bg-grey99 dark:bg-white/[0.06] ${isTopbar ? 'h-11 rounded-full p-1 pr-3' : 'h-[48px] rounded-2xl px-3'}`}>
                    <div className="animate-pulse flex items-center gap-2 w-full">
                        <div className="w-8 h-8 bg-grey90 dark:bg-white/10 rounded-full"></div>
                        <div className="h-3.5 bg-grey90 dark:bg-white/10 rounded-full flex-1"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Don't show selector if user has no organizations
    if (allOrganizations.length === 0) {
        return null;
    }

    const handleSelect = (orgId: string) => {
        setSelectedOrganizerId(orgId);
        setIsOpen(false);

        window.location.href = '/organizations/events';
    };

    const getOrgImage = (org: any) => {
        return org?.attachments?.avatar?.link || org?.profileImage || null;
    };

    const iconSlot = 'flex h-5 w-5 shrink-0 items-center justify-center [&_path]:stroke-current [&_svg]:h-5 [&_svg]:w-5';
    const divider = <div className="mx-3 my-1.5 h-px bg-grey95 dark:bg-nightBorder" />;
    const menuRowClass = (item: OrganizationSelectorMenuItem) =>
        `flex w-full items-center gap-3 rounded-full px-3 py-2.5 text-left text-sm font-medium transition-colors ${item.danger ? 'text-error40 hover:bg-grey99 dark:text-error60 dark:hover:bg-nightElevated' : item.active ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg' : 'text-grey10 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-nightElevated'}`;

    return (
        <div className={isTopbar ? 'relative' : 'w-full mb-4 relative'} ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={isTopbar
                    ? `flex items-center rounded-full ring-1 transition-all ${logoOnly ? 'p-0.5' : 'h-11 gap-2 p-1 pr-3'} ${isOpen ? 'ring-primary/60 dark:ring-nightAccent/60' : 'ring-grey90 hover:ring-grey70 dark:ring-nightBorder dark:hover:ring-nightMuted/50'}`
                    : 'w-full h-[48px] rounded-2xl bg-grey99 dark:bg-white/[0.06] ring-1 ring-black/[0.04] dark:ring-white/10 transition-colors flex items-center gap-3 pl-2 pr-3 py-2'
                }
            >
                {selectedOrg ? (
                    <>
                        <Avatar
                            src={getOrgImage(selectedOrg)}
                            name={selectedOrg.orgName}
                            seed={selectedOrg._id}
                            variant="org"
                            size={logoOnly ? 32 : 34}
                        />
                        {!logoOnly && (
                            <>
                                <span className={`text-sm font-semibold text-grey10 dark:text-white text-left truncate min-w-0 ${isTopbar ? 'max-w-[160px]' : 'flex-1'}`}>
                                    {selectedOrg.orgName}
                                </span>
                                <ChevronDownIcon size={16} className={`shrink-0 text-grey40 transition-transform dark:text-nightMuted ${isOpen ? 'rotate-180' : ''}`} />
                            </>
                        )}
                    </>
                ) : (
                    <span className={`text-sm font-medium text-grey40 dark:text-nightMuted text-left ${isTopbar ? 'px-3' : 'flex-1'}`}>
                        Select organization
                    </span>
                )}
            </button>

            {isOpen && (
                <div className={isTopbar
                    ? `absolute top-full ${align === 'left' ? 'left-0' : 'right-0'} mt-2 w-64 rounded-[28px] bg-white p-1.5 ring-1 ring-black/[0.06] shadow-[0_24px_60px_-12px_rgba(7,26,27,0.28)] dark:bg-nightSurface dark:ring-white/10 dark:shadow-[0_24px_60px_-12px_rgba(0,0,0,0.9)] z-50 max-h-[80vh] overflow-y-auto animate-fade-in`
                    : 'absolute top-full left-0 right-0 mt-2 rounded-[28px] bg-white p-1.5 ring-1 ring-black/[0.06] shadow-[0_24px_60px_-12px_rgba(7,26,27,0.28)] dark:bg-nightSurface dark:ring-white/10 z-50 max-h-64 overflow-y-auto'
                }>
                    {isTopbar && topMenuItems && topMenuItems.length > 0 && (
                        <div>
                            {topMenuItems.map((item) => (
                                <div key={item.key}>
                                    {item.divider && divider}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsOpen(false);
                                            item.onClick();
                                        }}
                                        className={menuRowClass(item)}
                                    >
                                        {item.icon && (
                                            <span className={iconSlot}>{item.icon}</span>
                                        )}
                                        {item.label}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {allOrganizations.length > 1 && (
                        <div>
                            {topMenuItems && topMenuItems.length > 0 && divider}
                            {allOrganizations.map((org) => {
                                const isSelected = org._id === selectedOrganizerId;
                                return (
                                    <button
                                        key={org._id}
                                        type="button"
                                        onClick={() => handleSelect(org._id)}
                                        className={`flex w-full items-center gap-3 rounded-full py-1.5 pl-1.5 pr-3 text-left transition-colors ${isSelected ? 'bg-grey99 dark:bg-nightElevated' : 'hover:bg-grey99 dark:hover:bg-nightElevated'}`}
                                    >
                                        <Avatar
                                            src={getOrgImage(org)}
                                            name={org.orgName}
                                            seed={org._id}
                                            variant="org"
                                            size={32}
                                        />
                                        <span className={`text-sm flex-1 truncate min-w-0 ${isSelected ? 'font-semibold text-grey10 dark:text-white' : 'font-medium text-grey10 dark:text-nightTextSecondary'}`}>
                                            {org.orgName}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {allOrganizations.length > 1 && isTopbar && menuItems && menuItems.length > 0 && divider}
                    {isTopbar && menuItems && menuItems.length > 0 && (
                        <div>
                            {menuItems.map((item) => (
                                <div key={item.key}>
                                    {item.divider && divider}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsOpen(false);
                                            item.onClick();
                                        }}
                                        className={menuRowClass(item)}
                                    >
                                        {item.icon && (
                                            <span className={iconSlot}>{item.icon}</span>
                                        )}
                                        {item.label}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
